import { GroupedControlRenderer } from "./controlPrimitives";

/**
 * @template TValue
 * @template {object} TOptions
 * @typedef {object} BaseControlDefinition
 *
 * @property {string} label - 'Nice' name of the control
 * @property {string} [description] - Description of the control
 * @property {TValue} defaultValue - Default value
 * @property {TOptions} [options] - Type-specific options
 * @property {Object.<string, BaseControl>} [children] - Nested controls
 * @property {(widget: import('../widgets').BaseWidget, groupValue?: any) => boolean} [visibleWhen] - Optional predicate; when it returns false the control is hidden from the settings panel. Re-evaluated on every render, so it can depend on sibling settings. For children of a grouped control the group's current value is passed as second argument.
 */

/**
 * @abstract
 * @template {any} [TValue=any]
 * @template {object} [TOptions=object]
 */
export class BaseControl {

    /** @type {boolean} */
    loaded = true;

    /** @type {Promise<void> | null} */
    loadPromise = null;
    
    /**
     * @param {BaseControlDefinition<TValue, TOptions | (() => Promise<TOptions>)>} options
     */
    constructor({
        label,
        description,
        defaultValue,
        // isRequired,
        options = /** @type {TOptions} */ ({}),
        children = {},
        visibleWhen,
    }) {
        this.label = label;
        this.description = description;
        this.defaultValue = defaultValue;
        // this.isRequired = isRequired || false;

        /** @type {TOptions} */
        this.options = /** @type {TOptions} */ (options);

        if (this.options instanceof Function || typeof this.options === 'function') {
            this.loaded = false;
        }

        /** @type {Object.<string, BaseControl>} */
        this.children = children;

        /** @type {((widget: import('../widgets').BaseWidget) => boolean) | null} */
        this.visibleWhen = visibleWhen || null;
    }

    /**
     * Rendering logic for the control
     * @abstract
     * @param {TValue} value - Current value of the control
     * @param {(value: TValue) => void} onChange - Callback for changes
     * @param {import('../widgets').BaseWidget} [_widget] - Owning widget (optional; used by controls that need to read sibling settings, e.g. NestedItemControl)
     * @return {React.ReactNode}
     */
    render(value, onChange, _widget) {
        return <GroupedControlRenderer
            label={this.label}
            value={value}
            onChange={onChange}
            controls={this.children}
            widget={_widget}
        />;
    }

    /**
     * Initializes the control to be rendered. 
     */
    load() {
        /* One read per instance, also when two renderers ask at once
           (StrictMode runs effects twice). A failure clears the cache so a
           later mount can retry; the rejection reaches the caller. */
        if (!this.loadPromise) {
            this.loadPromise = (async () => {
                if (typeof this.options === 'function') {
                    this.options = await /** @type {() => Promise<TOptions>} */ (this.options)();
                }
                this.loaded = true;
            })().catch((error) => {
                this.loadPromise = null;
                throw error;
            });
        }
        return this.loadPromise;
    }
}
